import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AdSlot } from '@/components/common/AdSlot';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Spinner } from '@/components/ui/Spinner';
import { useI18n } from '@/hooks/useI18n';
import { useDownloadStore } from '@/store/downloadStore';
import { useUiStore } from '@/store/uiStore';
import type { OutputType } from '@/types';

const ETA_BY_OUTPUT: Record<OutputType, string> = {
  video: '20–60 seconds',
  audio: '10–30 seconds',
  snapshots: '30–90 seconds',
};

/** "Preparing your media…" panel shown while a job is in flight. */
export function ProcessingOverlay() {
  const active = useUiStore((s) => s.processingActive);
  const queue = useDownloadStore((s) => s.queue);
  const { t } = useI18n();

  const currentOutput = queue.find((r) => r.id.startsWith('local-'))?.outputType ?? 'video';

  return createPortal(
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 48 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed inset-x-0 bottom-0 z-50 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-96"
        >
          <div className="glass-strong space-y-4 rounded-b-none border-primary-500/20 p-5 shadow-glow sm:rounded-2xl">
            <div className="flex items-center gap-3">
              <Spinner />
              <div>
                <p className="text-sm font-semibold text-zinc-100">{t('processing.title')}</p>
                <p className="text-xs text-zinc-500">{t('processing.subtitle')}</p>
              </div>
            </div>
            <ProgressBar indeterminate />
            <p className="text-xs text-zinc-500">
              {t('processing.eta')}:{' '}
              <span className="font-medium text-zinc-300">{ETA_BY_OUTPUT[currentOutput]}</span>
            </p>
            <AdSlot slot={import.meta.env.VITE_ADSENSE_SLOT_PROCESSING} className="h-24" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
